import { useRouter } from "next/router";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Icon from "@ui-kit/icons/Icon.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Menu from "@ui-kit/overlays/Menu/Menu.web";
import Text from "@ui-kit/typography/Text/Text.web";
import type { UserMenuSharedProps } from "./UserMenu.props";

const LOGOUT_VALUE = "__logout__";

export default function UserMenu(props: UserMenuSharedProps): JSX.Element {
  const {
    links,
    handleLogout,
    user,
    hideUserName = false,
    onMobileMenuOpen,
  } = props;
  const { t } = useTranslation("common");
  const router = useRouter();

  const handleSelect = (details: { value: string }) => {
    if (details.value === LOGOUT_VALUE) {
      handleLogout();
    } else {
      router.push(details.value);
    }
  };

  const profileImage = (
    <CDNImage
      width={27}
      height={27}
      alt={t("User profile image")}
      src={
        user?.profile_picture ??
        formatRelativeStaticPath("images/no-avatar.jpg")
      }
      data-testid="navbar-user-profile-picture"
      style={{
        userSelect: "none",
        borderRadius: "50px",
      }}
    />
  );

  const buttonContent = (
    <Flex alignItems="center" data-sentry-mask gap={{ base: "2xs", sm: "xs" }}>
      {profileImage}
      {!hideUserName && (
        <Text
          data-testid="navbar-user-name"
          fontSize="lg"
          display={{ base: "none", sm: "block" }}
          color="fg"
        >
          {user?.username} sdfsd
        </Text>
      )}
      <Icon.ChevronDown boxSize="element.md" color="fg" />
    </Flex>
  );

  const menuList = (
    <Menu.List data-testid="navbar-user-menu-list">
      {links.map((link) => (
        <Menu.Item
          key={link.label}
          value={link.href}
          data-testid={`navbar-user-menu-${link.label.toLowerCase()}-button`}
        >
          {link.label}
        </Menu.Item>
      ))}
      <Menu.Item
        value={LOGOUT_VALUE}
        data-testid="navbar-user-menu-logout-button"
        _hover={{
          _light: { bg: "red.700", color: "white" },
          _dark: { bg: "red.400", color: "white" },
        }}
      >
        {t("Log Out")}
      </Menu.Item>
    </Menu.List>
  );

  return (
    <>
      {/* Mobile: button that opens modal (only if onMobileMenuOpen provided) */}
      {onMobileMenuOpen && (
        <Box
          as="button"
          display={{ base: "flex", sm: "none" }}
          onClick={onMobileMenuOpen}
          data-testid="navbar-mobile-user-menu"
          h="element.3xl"
          px="md"
          borderRadius="sm"
          bg="transparent"
          color="fg"
          transition="all 0.2s"
          _hover={{
            _light: { bg: "gray.300" },
            _dark: { bg: "whiteAlpha.200" },
          }}
        >
          {buttonContent}
        </Box>
      )}

      {/* Desktop: dropdown menu (or always if no onMobileMenuOpen) */}
      <Box display={onMobileMenuOpen ? { base: "none", sm: "block" } : "block"}>
        <Menu onSelect={handleSelect}>
          <Menu.Button
            data-testid="navbar-user-menu-button"
            h="element.3xl"
            px="md"
            borderRadius="sm"
            transition="all 0.2s"
            _hover={{
              _light: { bg: "gray.300" },
              _dark: { bg: "whiteAlpha.200" },
            }}
          >
            <Flex
              alignItems="center"
              color="fg"
              gap={{ base: "2xs", sm: "xs" }}
            >
              {profileImage}
              {!hideUserName && (
                <Text
                  data-testid="navbar-user-name"
                  fontSize="md"
                  display={{ base: "none", sm: "block" }}
                  color="fg"
                >
                  {user?.username}
                </Text>
              )}
              <Icon.ChevronDown boxSize="element.md" color="fg" />
            </Flex>
          </Menu.Button>
          {menuList}
        </Menu>
      </Box>
    </>
  );
}
